import React, { useState } from "react";
import { useAuth } from "../../util/auth";
import { useTeamMembershipsByMember } from "../../util/db";
import { LockClosedIcon } from "@heroicons/react/24/outline";
import PaymentWrapperNode from "./PaymentWrapperNode";
import LoadingIcon from "../LoadingIcon";
import { Link } from "../../util/router";
import { getProductMeta } from "../../util/prices";

// Customer-facing names for the program each plan belongs to. Lifetime is
// all-access rather than a program of its own, so it never names the content.
const PROGRAM_NAMES = {
  proc: "Million Dollar Tool Kit",
  tp: "Agency Training",
  sb: "Stairs & Buckets",
  shuff: "Service Webinars",
};

// "Included in the Million Dollar Tool Kit and Agency Training", derived from
// the plans that unlock this content, so a locked tile says what to buy instead
// of reading as a broken account.
function describePrograms(requiredPlans = []) {
  const lines = [];
  for (const plan of requiredPlans) {
    const line = getProductMeta(plan)?.product_line;
    if (PROGRAM_NAMES[line] && !lines.includes(line)) lines.push(line);
  }
  const names = lines.map((line) => PROGRAM_NAMES[line]);
  if (names.length === 0) return null;
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

function haveCommonElements(arr1, arr2) {
  const set1 = new Set(arr1);
  for (const element of arr2) {
    if (set1.has(element)) {
      return true;
    }
  }
  return false;
}

export default function ValidatePaymentWrapper({ children, requiredPlans, small, tags = [], visible = true }) {
  const auth = useAuth();
  const [loading, setLoading] = useState(true);

  // Development bypass for users with devAccess flag in Firestore
  const isDevelopment = process.env.NODE_ENV === 'development';
  const hasDevAccess = auth.user.devAccess === true;

  const [isValid, setIsValid] = useState(
    (isDevelopment && hasDevAccess) || // Bypass in dev for users with devAccess flag
    (auth.user.legacyIsActive && (haveCommonElements(requiredPlans, auth.user.legacyPlans) || auth.user.legacyPlans.includes("life")))
    || (auth.user.planIsActive && (haveCommonElements(requiredPlans, auth.user.planIds) || auth.user.planIds.includes("life")))
    || (auth.user.planIsActive && haveCommonElements(auth.user.tags, tags))
  );

  // Query for team owners who have this user as a team member
  const {
    data: teamOwners,
    status: teamOwnersStatus,
  } = useTeamMembershipsByMember(auth.user.uid);

  const handleChildValueChange = (value) => {
    if (value) {
      setIsValid(value);
    }
    setLoading(false);
  };

  const programs = describePrograms(requiredPlans);

  const lockedContent = (
    <div className="relative">
      <div className="pointer-events-none opacity-50">
        {children}
      </div>
      {visible && (
        <div className={`absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 ${small ? "w-1/2" : "w-1/4"} `}>
          <div className="flex flex-col items-center text-center bg-white border p-4 rounded-lg space-y-2">
            <LockClosedIcon className={`text-gray-400 ${small ? "h-6" : "h-10"}`} />
            {small ? (
              <Link to="/all" className="text-sm font-semibold text-green-700 hover:text-green-600">
                Upgrade to unlock
              </Link>
            ) : (
              <>
                <p className="text-lg font-semibold text-gray-900">
                  {programs ? `Included in the ${programs}` : "Not included in your current plan"}
                </p>
                <p className="text-sm text-gray-500">
                  Your plan doesn't include this content yet.
                </p>
                <Link
                  to="/all"
                  className="mt-1 inline-block rounded-md bg-green-700 px-4 py-2 text-sm font-semibold text-white hover:bg-green-600"
                >
                  Upgrade to unlock
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // User has valid access (own subscription or granted via team owner)
  if (isValid) {
    return <>{children}</>;
  }

  // Team membership query still loading — show spinner instead of locks
  if (teamOwnersStatus === "loading") {
    return <LoadingIcon className="w-6" />;
  }

  // User is on a team — check if any team owner has a valid subscription
  const hasTeamOwners = teamOwnersStatus === "success" && teamOwners && teamOwners.length > 0;

  if (hasTeamOwners) {
    return (
      <>
        {teamOwners.map((owner, k) => (
          <PaymentWrapperNode
            requiredPlans={requiredPlans}
            onValueChange={handleChildValueChange}
            tags={tags}
            key={k}
            uid={owner.id}
          />
        ))}
        {loading ? <LoadingIcon className="w-6" /> : lockedContent}
      </>
    );
  }

  // No team owners found, or query errored — show lock
  return lockedContent;
}
